import { FIELD_LIMITS, MIN_FORM_SUBMIT_MS } from './constants';
import { isExpiryMonths } from './listingExpiry';
import { listingDisplayName } from './listingIdentity';
import {
  containsUnsafeMarkup,
  normaliseEmail,
  normalisePhone,
  normaliseWebsite,
  stripHtml,
} from './sanitize';
import type { ListingSubmission, ValidationResult } from './types';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[\d+\s()-]{6,30}$/;

const UNSAFE_MARKUP_MESSAGE = 'Remove HTML or scripting from this field';

const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const cleanTextField = (
  value: unknown,
  maxLength: number,
  field: string,
  errors: Record<string, string>,
): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  if (containsUnsafeMarkup(value)) {
    errors[field] = UNSAFE_MARKUP_MESSAGE;
    return undefined;
  }
  const cleaned = stripHtml(value);
  if (cleaned.length > maxLength) {
    errors[field] = `Must be ${maxLength} characters or fewer`;
    return undefined;
  }
  return cleaned.length > 0 ? cleaned : undefined;
};

export const validateSubmission = (
  input: unknown,
  options: {
    checkTiming?: boolean;
    requireTerms?: boolean;
    now?: number;
  } = {},
): ValidationResult => {
  const errors: Record<string, string> = {};
  const raw =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  // Reject unknown fields (companyName is accepted briefly then folded into name)
  const allowedKeys = new Set([
    'name',
    'serviceType',
    'companyName',
    'phone',
    'email',
    'website',
    'blurb',
    'imageKey',
    'expiryMonths',
    'websiteUrl',
    'formRenderedAt',
    'acceptedTerms',
  ]);
  for (const key of Object.keys(raw)) {
    if (!allowedKeys.has(key)) {
      errors._form = 'Unexpected field in submission';
      break;
    }
  }

  // Honeypot
  if (raw.websiteUrl && String(raw.websiteUrl).trim().length > 0) {
    errors._form = 'Submission rejected';
  }

  // Timing check
  if (options.checkTiming !== false) {
    const renderedAt =
      typeof raw.formRenderedAt === 'number' ? raw.formRenderedAt : 0;
    const now = options.now ?? Date.now();
    if (renderedAt > 0 && now - renderedAt < MIN_FORM_SUBMIT_MS) {
      errors._form = 'Please wait a moment before submitting';
    }
  }

  const cleanedName = cleanTextField(raw.name, FIELD_LIMITS.name, 'name', errors);
  const cleanedCompanyName = cleanTextField(
    raw.companyName,
    FIELD_LIMITS.name,
    'name',
    errors,
  );
  const mergedName = listingDisplayName({
    name: cleanedName,
    companyName: cleanedCompanyName,
  });

  const sanitized: ListingSubmission = {
    name: mergedName ? mergedName.slice(0, FIELD_LIMITS.name) : undefined,
    serviceType: cleanTextField(
      raw.serviceType,
      FIELD_LIMITS.serviceType,
      'serviceType',
      errors,
    ),
    blurb: cleanTextField(raw.blurb, FIELD_LIMITS.blurb, 'blurb', errors),
    imageKey:
      typeof raw.imageKey === 'string' && raw.imageKey.length <= 200
        ? raw.imageKey
        : undefined,
  };

  if (raw.phone !== undefined && raw.phone !== null && raw.phone !== '') {
    const phone = normalisePhone(String(raw.phone));
    if (!PHONE_PATTERN.test(phone)) {
      errors.phone = 'Enter a valid phone number';
    } else {
      sanitized.phone = phone.slice(0, FIELD_LIMITS.phone);
    }
  }

  if (raw.email !== undefined && raw.email !== null && raw.email !== '') {
    const email = normaliseEmail(String(raw.email));
    if (!EMAIL_PATTERN.test(email)) {
      errors.email = 'Enter a valid email address';
    } else {
      sanitized.email = email.slice(0, FIELD_LIMITS.email);
    }
  }

  if (raw.website !== undefined && raw.website !== null && raw.website !== '') {
    const website = normaliseWebsite(String(raw.website));
    if (!isValidUrl(website)) {
      errors.website = 'Enter a valid website URL';
    } else {
      sanitized.website = website.slice(0, FIELD_LIMITS.website);
    }
  }

  const hasIdentity = Boolean(sanitized.name);
  const hasServiceType = Boolean(sanitized.serviceType);
  const hasContact = Boolean(
    sanitized.phone || sanitized.email || sanitized.website,
  );

  if (!hasIdentity) {
    errors.name = errors.name ?? 'Provide a name or business';
  }
  if (!hasServiceType) {
    errors.serviceType = errors.serviceType ?? 'Provide a service type';
  }
  if (!hasContact) {
    errors.contact = 'Provide at least one contact method';
  }

  if (isExpiryMonths(raw.expiryMonths)) {
    sanitized.expiryMonths = raw.expiryMonths;
  } else {
    errors.expiryMonths = 'Choose how long the listing should stay live';
  }

  if (options.requireTerms !== false) {
    if (raw.acceptedTerms !== true) {
      errors.acceptedTerms = 'You must acknowledge the listing terms';
    }
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    sanitized,
  };
};
